import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MetricBox } from 'components/UI/V3/MetricBox/MetricBox';
import { Tooltip } from 'components/UI/V3/Tooltip/Tooltip';
import { FunctionComponent, ReactNode } from 'react';

import styles from './MetricsRow.module.scss';

interface MetricsRowProps {
    /** Delivered count — used as denominator for the rate formatting fallback. */
    sent: number;
    openRate: number;
    clickRate: number;
}

/**
 * Performance strip on the Campaign Detail view. Renders the three metrics the
 * backend exposes today (Delivered + Open Rate + Click Rate) using the V3
 * `MetricBox` component so the visual language matches other Cuddles metric
 * strips (Abandoned Bookings header, etc.). Bounce Rate and Unsubscribe Rate
 * are post-MVP and will drop in as additional `<MetricBox>` instances once BE
 * ships the tracking.
 *
 * Delivered leads because it's the denominator for the two rates — placing it
 * first mirrors the reading order of the CUD-4208 spec and gives the user a
 * quick baseline before the percentages.
 *
 * `MetricBox` doesn't expose a tooltip prop out of the box, so the info icon
 * + hover tooltip live inside the `title` ReactNode. Keeps the "explain the
 * metric on hover" affordance the design calls for without forking the
 * shared component.
 */
const MetricsRow: FunctionComponent<MetricsRowProps> = ({ sent, openRate, clickRate }) => {
    return (
        <div className={styles.root}>
            <MetricBox
                className={styles.metricBox}
                title="Delivered"
                value={sent.toLocaleString()}
            />
            <MetricBox
                className={styles.metricBox}
                title={renderTitle('Open Rate', 'The percentage of delivered emails that were opened by recipients.')}
                value={formatRate(openRate, sent)}
            />
            <MetricBox
                className={styles.metricBox}
                title={renderTitle('Click Rate', 'The percentage of delivered emails where a link was clicked.')}
                value={formatRate(clickRate, sent)}
            />
        </div>
    );
};

/**
 * "21.86%" for a non-empty send; "—" when nothing went out (a bounced-only
 * campaign, or one that failed entirely). Rendering "0.00%" in that edge
 * case would suggest "no engagement" when the real story is "no data yet".
 */
const formatRate = (rate: number, sent: number): string => {
    if (sent === 0) return '—';
    return `${(rate * 100).toFixed(2)}%`;
};

/**
 * Compose the title label + info icon + Tooltip in one place so each card
 * call stays a single line. The icon inherits the tooltip via V3 `<Tooltip>`,
 * which flips to tap-to-open on touch devices out of the box.
 */
const renderTitle = (label: string, tooltip: string): ReactNode => {
    return (
        <span className={styles.titleWithIcon}>
            {label}
            <Tooltip content={tooltip}>
                <span
                    className={styles.infoIcon}
                    aria-label={`About ${label}`}
                >
                    <FontAwesomeIcon icon={faCircleInfo} />
                </span>
            </Tooltip>
        </span>
    );
};

export default MetricsRow;
